import React from 'react'
import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/utilities/ui'
import { Media } from '@/components/Media'
import ThemeIcon from '@/components/ui/ThemeIcon'
import type { Media as MediaType } from '@/payload-types'

interface Project {
  id: string
  title: string
  shortDescription?: string
  projectDate?: string
  place?: string
  products?: Array<{ title: string } | string>
  featuredImage?: MediaType | { url: string } | string
  slug: string
}

interface DetailedProjectEntryProps {
  project: Project
  layout: 'left' | 'right'
  sourceBlock?: string
}

export default function DetailedProjectEntry({ project, layout, sourceBlock }: DetailedProjectEntryProps) {
  const isLeftLayout = layout === 'left'

  // Check if featuredImage is a Media object (has id property) or a simple object with url
  const featuredImageResource =
    typeof project.featuredImage === 'object' &&
    project.featuredImage !== null &&
    'id' in project.featuredImage
      ? (project.featuredImage as MediaType)
      : null

  // Extract products as tags
  const tags =
    Array.isArray(project.products) && project.products.length > 0
      ? project.products.map((p) => (typeof p === 'string' ? p : p.title || ''))
      : []

  return (
    <Link
      href={`/projekte/${project.slug}`}
      className="grid grid-cols-1 md:grid-cols-5 gap-8 items-center w-full md:min-h-[22rem] lg:min-h-[24rem] group"
      data-card-type="project"
      data-card-slug={project.slug}
      data-card-block={sourceBlock}
    >
      {/* Image - always above text on mobile, alternating left/right on desktop */}
      <div
        className={cn(
          'relative h-80 md:h-full order-1 overflow-hidden',
          isLeftLayout ? 'md:col-span-2 md:order-1' : 'md:col-span-2 md:order-2',
        )}
      >
        {featuredImageResource ? (
          <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.04]">
            <Media
              resource={featuredImageResource}
              fill
              pictureClassName="block h-full w-full"
              imgClassName="h-full w-full object-cover"
              size="(max-width: 768px) 100vw, 50vw"
              alt={featuredImageResource.alt || project.title}
            />
          </div>
        ) : (
          <div
            aria-hidden
            className="absolute inset-0 bg-[var(--color-muted,theme(colors.neutral.800))] flex items-center justify-center"
          >
            <ThemeIcon size="size-12" alt="" ariaHidden />
          </div>
        )}

        {/* Arrow button */}
        <div className="absolute bottom-0 right-0 w-12 h-12 bg-brand flex items-center justify-center">
          <ArrowUpRight className="w-6 h-6 text-white transition-transform duration-200 rotate-45 group-hover:rotate-90" />
        </div>
      </div>

      {/* Text content - always below image on mobile, alternating left/right on desktop */}
      <div
        className={cn(
          'flex flex-col gap-6 order-2',
          isLeftLayout ? 'md:col-span-3 md:order-2' : 'md:col-span-3 md:order-1',
        )}
      >
        {/* Header Group - Tags, Title, Metadata (closely related) */}
        <div className="flex flex-col gap-4">
          {/* Tags — first two always visible; remaining shown inline from md,
              collapsed into a "+N weitere" chip on mobile to avoid row overflow. */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {tags.slice(0, 2).map((tag, index) => (
                <span
                  key={`tag-${index}`}
                  className="bg-brand label-md font-ui leading-none whitespace-nowrap px-3 py-2 on-brand"
                >
                  {tag}
                </span>
              ))}
              {tags.slice(2).map((tag, index) => (
                <span
                  key={`tag-extra-${index}`}
                  className="hidden md:inline-flex bg-brand label-md font-ui leading-none whitespace-nowrap px-3 py-2 on-brand"
                >
                  {tag}
                </span>
              ))}
              {tags.length > 2 && (
                <span
                  aria-label={`${tags.length - 2} weitere Kategorien: ${tags.slice(2).join(', ')}`}
                  className="md:hidden bg-brand label-md font-ui leading-none whitespace-nowrap px-3 py-2 on-brand"
                >
                  +{tags.length - 2} weitere
                </span>
              )}
            </div>
          )}
          <div>
            <h2 className="heading-md text-primary text-balance transition-colors duration-300 group-hover:text-brand">
              {project.title}
            </h2>
            {project.place && (
              <div className="flex flex-wrap items-baseline gap-x-3 text-left">
                <span className="label-lg text-brand">{project.place}</span>
              </div>
            )}
            {project.shortDescription && (
              <p className="body-base text-secondary text-left">{project.shortDescription}</p>
            )}
          </div>
        </div>

        {/* Call-to-Action */}
        <div className="pt-4">
          <span className="inline-flex items-center gap-2 font-ui font-medium text-base text-text-brand group-hover:text-brand/80 transition-colors duration-300">
            <span className="uppercase">Projekt ansehen</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              className="transition-transform duration-300 group-hover:translate-x-1"
            >
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </div>
      </div>
    </Link>
  )
}
